import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router';
import { Box, Flex, Text, Icon } from 'roo-ui/components';
import NextLink from './NextLink';
import PreviousLink from './PreviousLink';
import Summary from './Summary';
import PageLinks from './PageLinks';
import { useDataLayer } from 'hooks/useDataLayer';
import { SORTS_BY_CODE } from 'lib/enums/search';

const Pagination = ({ pageSize, total, showSortOrder, ...rest }) => {
  const { emitInteractionEvent } = useDataLayer();
  const { pathname, search } = useLocation();
  const currentQuery = queryString.parse(search);
  const page = Number(currentQuery.page) || 1;

  const offset = pageSize * page - pageSize;
  const from = offset + 1;
  const to = from + pageSize - 1;

  const sortByName = SORTS_BY_CODE[currentQuery.sortBy]?.name;

  const onClick = useCallback(
    (page) => {
      emitInteractionEvent({ type: 'Page Number', value: `Page ${page}` });
    },
    [emitInteractionEvent],
  );

  return (
    <Flex
      flexDirection={['column', 'row', 'row']}
      justifyContent={['center', 'space-between', 'space-between']}
      width="100%"
      {...rest}
      data-testid="pagination"
    >
      <Flex justifyContent="center">
        <Summary from={from} to={to} total={total} />

        {showSortOrder && sortByName && (
          <Flex py={1}>
            <Box mx={2}>
              <Icon name="circle" color="greys.charcoal" size={4} />
            </Box>
            <Text data-testid="sorted-by">Sorted by {sortByName}</Text>
          </Flex>
        )}
      </Flex>

      <Flex alignItems="center" justifyContent="center" mt={[5, 0, 0]}>
        <PreviousLink page={page} pathname={pathname} currentQuery={currentQuery} onClick={onClick} />
        <PageLinks page={page} pathname={pathname} currentQuery={currentQuery} pageSize={pageSize} total={total} onClick={onClick} />
        <NextLink page={page} pathname={pathname} currentQuery={currentQuery} pageSize={pageSize} total={total} onClick={onClick} />
      </Flex>
    </Flex>
  );
};

Pagination.propTypes = {
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  showSortOrder: PropTypes.bool,
};

Pagination.defaultProps = {
  showSortOrder: false,
};

export default Pagination;
